
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Injectable, inject } from "@angular/core";
import { TaskState } from "../store/reducers/tasks.reducers";
import { Store } from "@ngrx/store";
import { getAuthUser } from "../store/selectors";
import { AuthService } from "../../common/providers/auth.service";
import { Observable, Subject, combineLatest, forkJoin } from "rxjs";
import { map, take } from "rxjs/operators";
import { CommonUtil, mobileonly } from "../../common/utils/common.utils";
import { Task, TaskComment, ICompact, Project, User, List, PendingOperation, Tag, SearchQuery, Location } from "../models";
import { AppConstants } from "../../common/utils/app-constants";
import { DatabaseReady } from "../../actions/app";
import { TaskUtils } from "../shared/task-utils";
import { LocalStorageService } from "./local-storage.service";
import { Broadcaster } from "../../common/providers/broadcaster.service";
import { FilesStorageService } from "./files-storage.service";
import { environment } from "../../../environments/environment";
import { IndexedDBService } from "./db/indexedDB.service";
import { StorageService } from "../../common/services/storage.service";



// Mechanical decomposition layer of DatabaseService (Q-02). Behavior-preserving.
export abstract class DatabaseStateBase {
  protected store = inject<Store<TaskState>>(Store);

  protected localStorageService = inject(LocalStorageService);

  protected broadcaster = inject(Broadcaster);

  protected filesStorageService = inject(FilesStorageService);

  protected KEY_SQLITE_DB = "vnc_task.db";
  // Tasks SQL
  //

  protected CREATE_TASK_TABLE_SQL = "CREATE TABLE IF NOT EXISTS TaskTable (id INTEGER not null, status_id integer not null, status_name text, author integer not null, assigned_to integer, start_date text, due_date text, subject text, priority integer, description text, project_id integer, comments text, list_id integer, location_id integer, tags_ids text, watcher_ids text, repeat text, external_url text, task text not null, created_on text)";

  protected CREATE_TASK_SQL = "INSERT INTO TaskTable(id, status_id, status_name, author, assigned_to, start_date, due_date, subject, priority, description, project_id, comments, list_id, location_id, tags_ids, watcher_ids, repeat, external_url, task, created_on) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)";

  protected UPDATE_TASK_SQL = "UPDATE TaskTable SET status_id = ?, status_name = ? , author = ?, assigned_to = ?, start_date = ?, due_date = ?, subject = ?, priority = ?, description = ?, project_id = ?, comments = ?, list_id = ?, location_id = ?, tags_ids = ?, watcher_ids = ?, repeat = ?, external_url = ?, task = ?, created_on = ? WHERE id = ?";

  protected UPDATE_TASK_TAGS_IDS_SQL = "UPDATE TaskTable SET tags_ids = ? WHERE id = ?";

  protected REPLACE_TASK_SQL = "UPDATE TaskTable SET id = ?, status_id = ?, status_name = ? , author = ?, assigned_to = ?, start_date = ?, due_date = ?, subject = ?, priority = ?, description = ?, project_id = ?, comments = ?, list_id = ?, location_id = ?, tags_ids = ?, watcher_ids = ?, repeat = ?, external_url = ?, task = ?, created_on = ? WHERE id = ?";

  protected DROP_TASK_TABLE_SQL = "DROP TABLE IF EXISTS TaskTable";

  protected DELETE_TASKS = "DELETE FROM TaskTable";
  //
  //

  protected FIND_TASK_BY_ID_SQL = "SELECT t.*, l.name AS list_name, o.name AS location_name from TaskTable t LEFT JOIN ListTable AS l ON t.list_id = l.id LEFT JOIN LocationTable as o ON t.location_id = o.id WHERE t.id = ?";

  protected FIND_TASKS_BY_ID_SQL  = "SELECT id FROM TaskTable where id IN ";

  protected FIND_TASKS_SQL = "SELECT due_date, task FROM TaskTable WHERE 1=1";
  //

  protected COUNT_TOTAL_TASKS_SQL = "SELECT count(id) AS cnt FROM TaskTable WHERE 1=1";

  protected COUNT_TOTAL_COMPLETED_SQL = "SELECT count(id) AS cnt FROM TaskTable WHERE 1=1 AND trim(lower(status_name)) = 'completed'";

  protected COUNT_TOTAL_OVERDUE_SQL = "SELECT count(id) AS cnt FROM TaskTable WHERE 1=1 AND date(due_date) BETWEEN date('1970-01-01') AND date('now','-1 day')";

  protected COUNT_TOTAL_DUE_DATE_SQL = "SELECT count(id) AS cnt FROM TaskTable WHERE 1=1 AND date(due_date) = ?";

  protected COUNT_ALL_TASKS_SQL = "SELECT count(id) AS cnt FROM TaskTable WHERE (author = ? OR assigned_to = ?)";

  protected COUNT_TASKS_BY_STATUS_SQL = "SELECT count(id) AS cnt FROM TaskTable WHERE (author = ? OR assigned_to = ?) AND trim(lower(status_name)) = ?";

  protected COUNT_TASKS_OPEN_SQL = "SELECT count(id) AS cnt FROM TaskTable WHERE (author = ? OR assigned_to = ?) AND trim(lower(status_name)) IN ('in progress', 'new')";

  protected COUNT_TASKS_NEW_SQL = "SELECT count(id) AS cnt FROM TaskTable WHERE (author = ? OR assigned_to = ?) AND trim(lower(status_name)) = 'new'";

  protected COUNT_TASKS_COMPLETED_SQL = "SELECT count(id) AS cnt FROM TaskTable WHERE (author = ? OR assigned_to = ?) AND trim(lower(status_name)) = 'completed'";

  protected COUNT_TASKS_BY_ASSIGNED_SQL = "SELECT count(id) AS cnt FROM TaskTable WHERE author != ? AND assigned_to = ?";

  protected COUNT_TASKS_BY_AUTHOR_SQL = "SELECT count(id) AS cnt FROM TaskTable WHERE author = ?";

  protected COUNT_TASKS_BY_DUE_DATE_SQL = "SELECT count(id) AS cnt FROM TaskTable WHERE (author = ? OR assigned_to = ?) AND date(due_date) = ?";

  protected COUNT_TASKS_BY_DUE_DATE_RANGE_SQL = "SELECT count(id) AS cnt FROM TaskTable WHERE (author = ? OR assigned_to = ?) AND date(due_date) BETWEEN ? AND ?";

  protected COUNT_TASKS_OVERDUE_SQL = "SELECT count(id) AS cnt FROM TaskTable WHERE (author = ? OR assigned_to = ?) AND date(due_date) BETWEEN date('1970-01-01') AND date('now','-1 day')";

  protected COUNT_TASKS_WATCH_SQL = "SELECT count(id) AS cnt FROM TaskTable WHERE watcher_ids LIKE";

  protected COUNT_TASKS_MY_OVERDUE_SQL = "SELECT count(id) AS cnt FROM TaskTable WHERE (author = ? OR assigned_to = ?) AND trim(lower(status_name)) IN ('in progress', 'new') AND date(due_date) <= ?";
  //

  protected UPDATE_TASK_STATUS_SQL = "UPDATE TaskTable SET status_id = ?, status_name = ?, task = ? WHERE id = ?";

  protected UPDATE_TASK_COMMENTS_SQL = "UPDATE TaskTable SET comments = ?, task = ? WHERE id = ?";

  protected UPDATE_TASK_PRIORITY_SQL = "UPDATE TaskTable SET priority = ?, task = ? WHERE id = ?";

  protected UPDATE_TASK_REPEAT_SQL = "UPDATE TaskTable SET repeat = ?, task = ? WHERE id = ?";

  protected UPDATE_TASK_STARTDATE_SQL = "UPDATE TaskTable SET start_date = ?, task = ? WHERE id = ?";

  protected UPDATE_TASK_DUEDATE_SQL = "UPDATE TaskTable SET due_date = ?, task = ? WHERE id = ?";

  protected UPDATE_TASK_PROJECTID_SQL = "UPDATE TaskTable SET project_id = ?, task = ? WHERE id = ?";

  protected UPDATE_TASK_REMOVE_LIST_SQL = "UPDATE TaskTable SET list_id = null WHERE list_id = ?";

  protected UPDATE_TASKS_LIST_SQL = "UPDATE TaskTable SET list_id = ? WHERE id IN ";

  protected UPDATE_TASKS_LOCATION_SQL = "UPDATE TaskTable SET location_id = ? WHERE id IN ";

  protected UPDATE_TASK_REMOVE_LOCATION_SQL = "UPDATE TaskTable SET location_id = null WHERE location_id = ?";

  protected UPDATE_TASK_WATCHER_SQL = "UPDATE TaskTable SET watcher_ids = ?, task = ? WHERE id = ?";
  // protected UPDATE_TASKS_ASSIGNEDTO_SQL = "UPDATE TaskTable SET assigned_to = ? WHERE id IN ";

  protected UPDATE_TASK_ASSIGNEDTO_SQL = "UPDATE TaskTable SET assigned_to = ?, task = ? WHERE id = ?";
  //

  protected DELETE_TASKS_SQL = "DELETE from TaskTable where id IN ";
  // Avatars SQL
  //

  protected CREATE_AVATAR_TABLE_SQL = "CREATE TABLE IF NOT EXISTS AvatarTable (user_id INTEGER not null, local_file_name text not null, last_update_ts INTEGER not null)";

  protected FIND_AVATAR_SQL = "SELECT local_file_name FROM AvatarTable where user_id = ?";

  protected FIND_AVATARS_BY_USER_ID_SQL = "SELECT user_id, local_file_name FROM AvatarTable where user_id IN ";

  protected CREATE_AVATAR_IF_NOT_EXIST_SQL = "INSERT INTO AvatarTable(user_id, local_file_name, last_update_ts) SELECT ?, ?, ?  WHERE NOT EXISTS(SELECT 1 FROM AvatarTable WHERE user_id = ?)";

  protected UPDATE_AVATAR_SQL = "UPDATE AvatarTable SET local_file_name = ?, last_update_ts = ? WHERE user_id = ?";

  protected DROP_AVATAR_TABLE_SQL = "DROP TABLE IF EXISTS AvatarTable";

  protected DELETE_AVATARS = "DELETE FROM AvatarTable";

  protected DELETE_AVATAR = "DELETE FROM AvatarTable where user_id = ?";
  // Projects SQL
  //

  protected CREATE_PROJECT_TABLE_SQL = "CREATE TABLE IF NOT EXISTS ProjectTable (id INTEGER not null, project text not null, members text)";

  protected FIND_PROJECT_SQL = "SELECT project FROM ProjectTable where id = ?";

  protected FIND_PROJECT_MEMBERS_SQL = "SELECT members FROM ProjectTable where id = ?";

  protected SELECT_ALL_PROJECT_SQL = "SELECT id, project FROM ProjectTable";

  protected FIND_PROJECTS_IDS_BY_IDS_SQL = "SELECT id from ProjectTable WHERE id IN ";

  protected CREATE_PROJECT_SQL = "INSERT INTO ProjectTable(id, project, members) VALUES (?, ?, ?)";

  protected UPDATE_PROJECT_SQL = "UPDATE ProjectTable SET project = ?, members = ? WHERE id = ?";

  protected DROP_PROJECT_TABLE_SQL = "DROP TABLE IF EXISTS ProjectTable";

  protected DELETE_PROJECTS = "DELETE FROM ProjectTable";
  // Lists SQL
  //

  protected CREATE_LISTS_TABLE_SQL = "CREATE TABLE IF NOT EXISTS ListTable (id INTEGER not null, name text not null)";

  protected FIND_LISTS_IDS_BY_IDS_SQL = "SELECT id from ListTable WHERE id IN ";

  protected CREATE_LIST_SQL = "INSERT INTO ListTable(id, name) VALUES (?, ?)";

  protected UPDATE_LIST_SQL = "UPDATE ListTable SET name = ? WHERE id = ?";

  protected DELETE_LIST_SQL = "DELETE from ListTable WHERE id = ?";

  protected SELECT_ALL_LISTS_WITH_TASKS_COUNT_SQL = "SELECT l.name AS name, l.id AS id, COUNT(t.list_id) AS tasks_count FROM ListTable l LEFT JOIN TaskTable AS t on l.id = t.list_id GROUP BY l.name";

  protected DROP_LISTS_TABLE_SQL = "DROP TABLE IF EXISTS ListTable";

  protected DELETE_LISTS = "DELETE FROM ListTable";
   // Lists SQL
  //

  protected CREATE_LOCATIONS_TABLE_SQL = "CREATE TABLE IF NOT EXISTS LocationTable (id INTEGER not null, name text not null)";

  protected FIND_LOCATIONS_IDS_BY_IDS_SQL = "SELECT id from LocationTable WHERE id IN ";

  protected CREATE_LOCATION_SQL = "INSERT INTO LocationTable(id, name) VALUES (?, ?)";

  protected UPDATE_LOCATION_SQL = "UPDATE LocationTable SET name = ? WHERE id = ?";

  protected DELETE_LOCATION_SQL = "DELETE from LocationTable WHERE id = ?";

  protected SELECT_ALL_LOCATIONS_WITH_TASKS_COUNT_SQL = "SELECT l.name AS name, l.id AS id, COUNT(t.location_id) AS tasks_count FROM LocationTable l LEFT JOIN TaskTable AS t on l.id = t.location_id GROUP BY l.name";

  protected DROP_LOCATIONS_TABLE_SQL = "DROP TABLE IF EXISTS LocationTable";

  protected DELETE_LOCATIONS = "DELETE FROM LocationTable";
  // Tags SQL
  //

  protected CREATE_TASK_TAG_TABLE_SQL = "CREATE TABLE IF NOT EXISTS TaskTagTable (task_id INTEGER, tag_id INTEGER)";

  protected CREATE_TASK_TAG_IF_NOT_EXIST_SQL = "INSERT INTO TaskTagTable(task_id, tag_id) SELECT ?, ? WHERE NOT EXISTS(SELECT 1 FROM TaskTagTable WHERE task_id = ? AND tag_id = ?)";

  protected DELETE_TASK_TAG_BY_TAG_SQL = "DELETE from TaskTagTable WHERE tag_id = ?";

  protected DELETE_TASK_TAG_BY_TAKS_AND_TAG_SQL = "DELETE from TaskTagTable WHERE task_id = ? AND tag_id = ?";

  protected DELETE_TASK_TAG_BY_TASK_SQL = "DELETE from TaskTagTable WHERE task_id IN ";

  protected REPLACE_TASK_TAG_ID_SQL = "UPDATE TaskTagTable SET tag_id = ? WHERE tag_id = ?";

  protected SELECT_ALL_TAGS_WITH_TASKS_COUNT_SQL = "SELECT t.id,t.name, COUNT(tt.task_id) AS tasks_count FROM TaskTagTable tt JOIN TagTable AS t ON tt.tag_id=t.id GROUP BY t.id";

  protected DELETE_TAGS_TASKS = "DELETE FROM TaskTagTable";
  //

  protected CREATE_TAGS_TABLE_SQL = "CREATE TABLE IF NOT EXISTS TagTable (id INTEGER PRIMARY KEY, name text not null)";

  protected CREATE_TAG_IF_NOT_EXIST_SQL = "INSERT INTO TagTable(id, name) SELECT ?, ? WHERE NOT EXISTS(SELECT 1 FROM TagTable WHERE id = ?)";

  protected DELETE_TAG_SQL = "DELETE from TagTable WHERE id = ?";

  protected REPLACE_TAG_ID_SQL = "UPDATE TagTable SET id = ? WHERE id = ?";

  protected SELECT_TAGS_FOR_TASK = "SELECT id,name FROM TaskTagTable JOIN TagTable ON TaskTagTable.tag_id=TagTable.id WHERE task_id = ?";

  protected SELECT_TAGS_FOR_TASKS = "SELECT task_id,id,name FROM TaskTagTable JOIN TagTable ON TaskTagTable.tag_id=TagTable.id WHERE task_id IN ";

  protected SEARCH_TAGS_FOR_TASKS = "SELECT * FROM TagTable WHERE name LIKE ";

  protected SELECT_ALL_TAGS = "SELECT * FROM TagTable";

  protected DELETE_TAGS = "DELETE FROM TagTable";
  // Pending operations SQL
  //

  protected CREATE_PENDING_OPERATIONS_TABLE_SQL = "CREATE TABLE IF NOT EXISTS PendingOperationsTable (task_id INTEGER not null, type text not null, additional_data text)";

  protected ADD_PENDING_OPERATION_SQL = "INSERT INTO PendingOperationsTable(task_id, type, additional_data) VALUES (?, ?, ?)";

  protected UPDATE_PENDING_OPERATION_ADDITIONALDATA_SQL = "UPDATE PendingOperationsTable SET additional_data = ? WHERE task_id = ?";

  protected FIND_PENDING_OPERATION_BY_ID_SQL = "SELECT task_id, type, additional_data FROM PendingOperationsTable where task_id = ?";

  protected FIND_PENDING_OPERATION_ID_BY_ID_SQL = "SELECT task_id FROM PendingOperationsTable where task_id = ?";

  protected SELECT_ALL_PENDING_OPERATIONS_SQL = "SELECT task_id, type, additional_data FROM PendingOperationsTable";

  protected DELETE_PENDING_OPERATION_SQL = "DELETE from PendingOperationsTable where task_id = ?";

  protected DELETE_PENDING_OPERATIONS_SQL = "DELETE from PendingOperationsTable where task_id IN (?)";

  protected DROP_PENDING_OPERATIONS_TABLE_SQL = "DROP TABLE IF EXISTS PendingOperationsTable";

  protected DELETE_PENDING_OPS = "DELETE FROM PendingOperationsTable";
  // Save Search SQL
  //

  protected CREATE_SAVE_SEARCHES_TABLE_SQL = "CREATE TABLE IF NOT EXISTS SaveSearchTable (id INTEGER not null, name text not null, created_on text, issues_count integer, filters text)";

  protected CREATE_SAVE_SEARCHES_SQL = "INSERT INTO SaveSearchTable(id, name, created_on, issues_count, filters) VALUES (?, ?, ?, ?, ?)";

  protected FIND_SAVE_SEARCHES_IDS_BY_IDS_SQL = "SELECT id from SaveSearchTable WHERE id IN ";

  protected UPDATE_SAVE_SEARCHES_SQL = "UPDATE SaveSearchTable SET name = ?, created_on = ?, issues_count = ?, filters = ? WHERE id = ?";

  protected DROP_SAVE_SEARCHES_TABLE_SQL = "DROP TABLE IF EXISTS SaveSearchTable";

  protected DELETE_SAVE_SEARCHES = "DELETE FROM SaveSearchTable";

  protected SELECT_SAVE_SEARCHES = "SELECT * FROM SaveSearchTable";
  // Migration Sql

  protected MIGRATION_TASK_TABLE_ADD_WATCHER_IDS_FIELD_SQL = "ALTER TABLE TaskTable ADD COLUMN watcher_ids TEXT default null";

  protected MIGRATION_TASK_TABLE_ADD_LOCATION_ID_FIELD_SQL = "ALTER TABLE TaskTable ADD COLUMN location_id integer default null";

  protected MIGRATION_TASK_TABLE_ADD_EXTERNAL_URL_FIELD_SQL = "ALTER TABLE TaskTable ADD COLUMN external_url TEXT default null";

  protected MIGRATION_TASK_TABLE_ADD_CREATED_ON_FIELD_SQL = "ALTER TABLE TaskTable ADD COLUMN created_on TEXT default null";

  protected CREATE_MIGRATION_TABLE_SQL = "CREATE TABLE IF NOT EXISTS MigrationTable ( migration_id text, sql text not null, table_name text, migration_date text)";

  protected CREATE_MIGRATION_SQL = "INSERT INTO MigrationTable(migration_id, sql, table_name, migration_date) VALUES (?, ?, ?, ?)";

  protected SELECT_MIGRATION_IDS_SQL = "SELECT migration_id FROM MigrationTable";

  protected MIGRATION_IDS = [
    { id : "201904151350", sql: this.MIGRATION_TASK_TABLE_ADD_LOCATION_ID_FIELD_SQL, table: "TaskTable" },
    { id : "201904151351", sql: this.MIGRATION_TASK_TABLE_ADD_WATCHER_IDS_FIELD_SQL, table: "TaskTable" },
    { id : "201904151352", sql: this.MIGRATION_TASK_TABLE_ADD_CREATED_ON_FIELD_SQL, table: "TaskTable" }
  ];

  protected sqliteDB;

  protected isDbInitialized: boolean = false;

  protected storageLocation;

  protected dbServiceImpl;

  avatar = {};

}
